import { useCallback, useEffect, useRef } from 'react';
import _ from 'underscore';
import { type LogEventProps } from '../types';

export interface UseFeatureLoggerProps {
  conversationId: string;
  logger: any;
}

function stringifyIfObject(value: any): any {
  if (typeof value === 'object' && !Array.isArray(value)) {
    return JSON.stringify(value);
  }
  return value;
}

export function useFeatureLogger({
  conversationId,
  logger,
}: UseFeatureLoggerProps) {
  const conversationIdRef = useRef<UseFeatureLoggerProps['conversationId']>(conversationId);

  /**
   * * This effect is used to update the conversationIdRef whenever the conversationId prop changes.
   * * This is important for error & trace logging to use ref instead of props.conversationId to avoid stale closure issues.
   * * This is because the error & trace functions are created only once when the component mounts,
   * * and if the conversationId prop changes later, it won't be reflected in the error & trace functions.
   */
  useEffect(() => {
    conversationIdRef.current = conversationId;
  }, [conversationId]);

  const error = useCallback(({ featureName, data }: LogEventProps) => {
    const stringifiedData = _.mapObject(data, stringifyIfObject);
    const logData = {
      featureName,
      conversationId: conversationIdRef.current,
      ...stringifiedData,
    };

    const featureNamePrefix = featureName ? `[${featureName}]` : '';

    logger.error(`[Copilot]${featureNamePrefix}: ${JSON.stringify(logData)}`);
  }, [logger]);

  const trace = useCallback(({ featureName, data }: LogEventProps) => {
    const stringifiedData = _.mapObject(data, stringifyIfObject);
    const logData = {
      featureName,
      conversationId: conversationIdRef.current,
      ...stringifiedData,
    };

    const featureNamePrefix = featureName ? `[${featureName}]` : '';

    logger.trace(`[Copilot]${featureNamePrefix}: ${JSON.stringify(logData)}`);
  }, [logger]);

  return {
    error,
    trace,
  };
}
